import { createEffect, createSignal, onMount, untrack } from "solid-js";
import { animate } from "motion";
import {
  CtaState,
  DenominationProps,
} from "~/components/denomination_grid/denominations_grid";
import {
  DiscountDetails,
  DiscountDetailsItem,
  SimulatedTransactionDetails,
  SimulatedTransactionResponse,
} from "~/server/types/brand";
import { Optional } from "~/types";
import { clientIdConfig } from "~/data/partner_config";

export type SimulateParams = {
  voucherProductId: string;
  amount: number;
  variantId: string;
  consumeCoins: boolean;
  subscriptionDetails?: { tenure: number };
};

export type SimulateFunction = (
  params: SimulateParams
) => Promise<SimulatedTransactionResponse>;

export type InitialSimulationData = {
  selectedAmount: number | null;
  selectedVariantId: string;
  dueAmount: number | null;
  discountDetails: DiscountDetails | null;
  redemptionAmount: number | null;
  dueAmount2?: number | null;
  discountDetails2?: DiscountDetails | null;
  redemptionAmount2?: number | null;
  hubbleCoinRewards: DiscountDetailsItem | null;
  initialErrorOnSimulate: string | null;
  offerConstructTenure: number | null;
};

export function useDenominationGridState(props: DenominationProps) {
  const {
    brand,
    initialSimulationData,
    simulateFunction,
    getMode,
    getClientId,
    offerConstruct,
  } = props;

  // Internal state signals
  const [selectedAmount, setSelectedAmount] = createSignal<Optional<number>>(
    initialSimulationData.selectedAmount
  );

  const [selectedVariantId, setSelectedVariantId] = createSignal<
    Optional<string>
  >(initialSimulationData.selectedVariantId);

  const [rewardDiscountAmount, setRewardDiscountAmount] = createSignal(0);

  const [initialErrorOnSimulate, setInitialErrorOnSimulate] = createSignal<
    Optional<string>
  >(initialSimulationData.initialErrorOnSimulate);

  const [dryRunErrorMessage, setDryRunErrorMessage] =
    createSignal<Optional<string>>(null);

  const [dueAmount, setDueAmount] = createSignal<Optional<number>>(
    initialSimulationData.dueAmount
  );
  const [discountDetails, setDiscountDetails] = createSignal<
    Optional<DiscountDetails>
  >(initialSimulationData.discountDetails);
  const [redemptionAmount, setRedemptionAmount] = createSignal<
    Optional<number>
  >(initialSimulationData.redemptionAmount);

  const [dueAmount2, setDueAmount2] = createSignal<Optional<number>>(
    initialSimulationData.dueAmount2 ?? null
  );
  const [discountDetails2, setDiscountDetails2] = createSignal<
    Optional<DiscountDetails>
  >(initialSimulationData.discountDetails2 ?? null);
  const [redemptionAmount2, setRedemptionAmount2] = createSignal<
    Optional<number>
  >(initialSimulationData.redemptionAmount2 ?? null);

  const [offerConstructTenure, setOfferConstructTenure] = createSignal<
    Optional<number>
  >(initialSimulationData.offerConstructTenure);

  const [_ctaState, _setCtaState] = createSignal<CtaState>("DEFAULT");

  const ctaState = props.ctaState ?? _ctaState;
  const setCtaState = props.setCtaState ?? _setCtaState;

  const [isSimulating, setIsSimulating] = createSignal<boolean>(false);

  const [useRewardCoins, setUseRewardCoins] = createSignal<null | boolean>(
    getMode() == "rnr"
  );

  const [voucherCount, setVoucherCount] = createSignal<number>(0);

  const [hubbleCoinRewards, setHubbleCoinRewards] =
    createSignal<null | DiscountDetailsItem>(
      initialSimulationData.hubbleCoinRewards
    );

  // Set default offer construct tenure if subscription details are available
  createEffect(() => {
    const subscriptionDetails = offerConstruct?.subscriptionRewardDetails;
    if (subscriptionDetails && !offerConstructTenure()) {
      const defaultTenure =
        subscriptionDetails.find((detail: any) => detail.isDefault)?.tenure ??
        subscriptionDetails[0]?.tenure;
      setOfferConstructTenure(defaultTenure ?? null);
    }
  });

  function calculateVoucherCount(denominationDetails: any): number {
    return denominationDetails.reduce(
      (total: any, item: any) => total + (item.quantity ?? 0),
      0
    );
  }

  function updateVoucherInfo(simulateResponse: any) {
    const denominationDetails = simulateResponse?.denominationDetails;
    if (denominationDetails && Array.isArray(denominationDetails)) {
      const count = calculateVoucherCount(denominationDetails);
      setVoucherCount(count);
    }
  }

  function getDueAmount(
    simulatedTransactionDetails: SimulatedTransactionDetails
  ) {
    if (simulatedTransactionDetails.paymentSource == "COINS_ONLY") {
      return simulatedTransactionDetails.totalRequiredCoins;
    } else {
      return simulatedTransactionDetails.dueAmount;
    }
  }

  // Reset state when initial data changes
  createEffect(() => {
    setHubbleCoinRewards(initialSimulationData.hubbleCoinRewards);
    setSelectedVariantId(initialSimulationData.selectedVariantId);
    setDueAmount(initialSimulationData.dueAmount);
    setDiscountDetails(initialSimulationData.discountDetails);
    setRedemptionAmount(initialSimulationData.redemptionAmount);
    setDueAmount2(initialSimulationData.dueAmount2 ?? null);
    setDiscountDetails2(initialSimulationData.discountDetails2 ?? null);
    setRedemptionAmount2(initialSimulationData.redemptionAmount2 ?? null);
    setInitialErrorOnSimulate(initialSimulationData.initialErrorOnSimulate);
    setDryRunErrorMessage(null);
    setSelectedAmount(initialSimulationData.selectedAmount);
  });

  createEffect(async () => {
    const newAmount = selectedAmount();
    const rewardCoins = useRewardCoins();

    if (!newAmount) return;
    await untrack(async () => {
      // if (newAmount == redemptionAmount()) {
      //   setDryRunErrorMessage(null);
      //   return;
      // }
      setIsSimulating(true);
      setCtaState("DISABLED");

      try {
        if (
          getClientId() === clientIdConfig.tdClientId &&
          offerConstruct?.type === "SUBSCRIPTION_REWARD" &&
          offerConstruct?.subscriptionRewardDetails
        ) {
          const subscriptionDetails =
            offerConstruct?.subscriptionRewardDetails ?? [];
          const simulatePromises = subscriptionDetails.map((detail: any) =>
            simulateFunction({
              voucherProductId: brand.externalId,
              amount: newAmount,
              variantId: selectedVariantId() ?? "",
              consumeCoins: rewardCoins === true,
              subscriptionDetails: { tenure: detail.tenure },
            })
          );

          const simulateResponses = await Promise.all(simulatePromises);
          const newSimulateTransactionDetails = simulateResponses.map(
            (response) =>
              response.data.filter(
                (data: any) => data.paymentRoute != "WALLET"
              )[0]
          );
          updateVoucherInfo(newSimulateTransactionDetails[0]);

          if (subscriptionDetails.length === 1) {
            setDueAmount(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount(
              newSimulateTransactionDetails[0].redemptionAmount
            );
            setDueAmount2(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails2(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount2(
              newSimulateTransactionDetails[0].redemptionAmount
            );
          } else {
            setDueAmount(getDueAmount(newSimulateTransactionDetails[1]));
            setDiscountDetails(
              newSimulateTransactionDetails[1].discountDetails ?? null
            );
            setRedemptionAmount(
              newSimulateTransactionDetails[1].redemptionAmount
            );
            setDueAmount2(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails2(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount2(
              newSimulateTransactionDetails[0].redemptionAmount
            );
          }

          const hubbleRewardsDetails = simulateResponses.map(
            (response) =>
              response.data
                .filter(({ paymentMode }: any) => paymentMode === "UPI")
                .map(({ discountDetails }: any) => {
                  const { totalDiscount, coinDiscount } = discountDetails;
                  return {
                    hubbleCoinRewards: {
                      amount: totalDiscount.amount - coinDiscount.amount,
                      percentage:
                        totalDiscount.percentage - coinDiscount.percentage,
                    },
                  };
                })[0]
          );

          setHubbleCoinRewards(hubbleRewardsDetails[0].hubbleCoinRewards);
          setRewardDiscountAmount(
            newSimulateTransactionDetails[0].discountDetails?.coinDiscount
              .amount ?? 0
          );
          setDryRunErrorMessage(null);
        } else {
          const newSimulateResponse = await simulateFunction({
            voucherProductId: brand.externalId,
            amount: newAmount,
            variantId: selectedVariantId() ?? "",
            consumeCoins: rewardCoins === true,
          });

          const newSimulateTransactionDetails = newSimulateResponse.data.filter(
            (data: any) => data.paymentRoute != "WALLET"
          )[0];

          updateVoucherInfo(newSimulateTransactionDetails);

          const newSimulateHubbleRewardsDetails = newSimulateResponse.data
            .filter(({ paymentMode }: any) => paymentMode === "UPI")
            .map(({ discountDetails }: any) => {
              const { totalDiscount, coinDiscount } = discountDetails;
              return {
                hubbleCoinRewards: {
                  amount: totalDiscount.amount - coinDiscount.amount,
                  percentage:
                    totalDiscount.percentage - coinDiscount.percentage,
                },
              };
            })[0];

          setHubbleCoinRewards(
            newSimulateHubbleRewardsDetails?.hubbleCoinRewards
          );
          setRewardDiscountAmount(
            newSimulateTransactionDetails.discountDetails?.coinDiscount
              .amount ?? 0
          );

          const newDueAmount = getDueAmount(newSimulateTransactionDetails);
          if (getClientId() !== clientIdConfig.tdClientId) {
            // do not animate for td client
            animateAmount(dueAmount()!, newDueAmount, () => {});
          }
          setDueAmount(newDueAmount);
          setDiscountDetails(
            newSimulateTransactionDetails.discountDetails ?? null
          );
          setRedemptionAmount(newSimulateTransactionDetails.redemptionAmount);
          setDryRunErrorMessage(null);
        }
      } catch (e: any) {
        console.log(`got error ${e}`);
        console.log(`got error ${e.message}`);
        setDryRunErrorMessage(e.message);
      } finally {
        setIsSimulating(false);
        setCtaState("DEFAULT");
      }
    });
  });

  function animateAmount(
    currentAmount: number,
    newAmount: number,
    onAnimationComplete: () => void
  ) {
    const start = currentAmount;
    const diff = newAmount - start;
    const elements = document.querySelectorAll(".anim-due-amount")!;
    animate(
      (progress) => {
        if (elements[0]?.innerHTML && elements[1]?.innerHTML) {
          if (progress == 1) {
            onAnimationComplete();
            elements[0].innerHTML = "" + newAmount;
            elements[1].innerHTML = "" + newAmount;
          } else {
            const toAdd = progress * diff;
            elements[0].innerHTML = "" + (start + toAdd).toFixed(0);
            elements[1].innerHTML = "" + (start + toAdd).toFixed(0);
          }
        } else if (progress == 1) {
          onAnimationComplete();
        }
      },
      {
        duration: 0.5,
        easing: "linear",
      }
    );
  }

  return {
    // State accessors
    selectedAmount,
    setSelectedAmount,
    selectedVariantId,
    setSelectedVariantId,
    rewardDiscountAmount,
    initialErrorOnSimulate,
    dryRunErrorMessage,
    dueAmount,
    discountDetails,
    redemptionAmount,
    dueAmount2,
    discountDetails2,
    redemptionAmount2,
    offerConstructTenure,
    setOfferConstructTenure,
    ctaState,
    isSimulating,
    useRewardCoins,
    setUseRewardCoins,
    hubbleCoinRewards,
    voucherCount,
    // Methods
    // simulateTransaction,
  };
}
